import { isDirection, type Direction } from "../movement/step.ts";
import { HEADER_BYTES, OK, checkHeader, viewOf, writeHeader, type DecodeVerdict } from "./binary.ts";
import { MessageType } from "./catalogue.ts";
import { MAX_U32 } from "./limits.ts";

/**
 * The `input` frame (PROTOCOL.md 5.1): 13 bytes, client to server, one movement intent and the client's
 * snapshot ack.
 */

/**
 * Total frame length.
 */
export const INPUT_BYTES = 13;

/**
 * Flag bits of the `flags` byte; the rest are reserved and must be zero. `Sprint`: the page asks to run with this
 * input, and the map server alone decides whether its step is a run step (ADR-116 decisions 3 and 13). `Used`: the
 * player pressed a key or a pointer button on the page since the page's last input; `Away`: the page is hidden or has no
 * focus now (ADR-090 decision 3). A page sets either only on a connection whose gateway offered them with `away`, since
 * a gateway before protocol 6.16.0 drops an input with a reserved bit. `Jump`: the player pressed jump with this input
 * (ADR-116 decision 18, protocol 6.37.0); a gateway before it drops the input, which the rules version keeps a page of
 * this build from meeting (decision 16).
 */
export const InputFlag = { Sprint: 1, Used: 2, Away: 4, Jump: 8 } as const;

const KNOWN_FLAGS = InputFlag.Sprint | InputFlag.Used | InputFlag.Away | InputFlag.Jump;

/**
 * A decoded input, reused across frames on the server.
 */
export interface InputRecord {
  seq: number;
  ackTick: number;
  dir: Direction;
  flags: number;
}

/**
 * A fresh record for `decodeInput` to fill.
 *
 * @returns Zeroed record.
 */
export function newInputRecord(): InputRecord {
  return { seq: 0, ackTick: 0, dir: 0, flags: 0 };
}

/**
 * Encodes an input into a caller-owned buffer.
 *
 * @param buffer Destination, at least `INPUT_BYTES` long.
 * @param input Values to write.
 * @returns Bytes written.
 * @throws Error when the buffer is too small or a value does not fit its field.
 */
export function encodeInput(buffer: Uint8Array, input: Readonly<InputRecord>): number {
  if (buffer.length < INPUT_BYTES) {
    throw new Error(`buffer of ${buffer.length} bytes cannot hold a ${INPUT_BYTES}-byte input`);
  }
  if (!Number.isInteger(input.seq) || input.seq < 0 || input.seq > MAX_U32) {
    throw new Error(`seq ${input.seq} is not a u32`);
  }
  if (!Number.isInteger(input.ackTick) || input.ackTick < 0 || input.ackTick > MAX_U32) {
    throw new Error(`ackTick ${input.ackTick} is not a u32`);
  }
  if (!isDirection(input.dir)) {
    throw new Error(`dir ${input.dir} is not a direction`);
  }
  if (!Number.isInteger(input.flags) || (input.flags & ~KNOWN_FLAGS) !== 0) {
    throw new Error(`flags ${input.flags} set reserved bits`);
  }
  const view = viewOf(buffer);
  writeHeader(view, MessageType.Input);
  view.setUint32(HEADER_BYTES, input.seq, true);
  view.setUint32(HEADER_BYTES + 4, input.ackTick, true);
  view.setUint8(HEADER_BYTES + 8, input.dir);
  view.setUint8(HEADER_BYTES + 9, input.flags);
  return INPUT_BYTES;
}

/**
 * Decodes an input frame into a record. Never throws: a wrong length or type closes, a bad direction or
 * reserved flag bit drops (PROTOCOL.md section 7).
 *
 * @param frame The whole frame.
 * @param out Record to fill; untouched unless the verdict is `ok`.
 * @returns The verdict.
 */
export function decodeInput(frame: Uint8Array, out: InputRecord): DecodeVerdict {
  const header = checkHeader(frame, MessageType.Input, INPUT_BYTES);
  if (header.kind !== "ok") {
    return header;
  }
  const view = viewOf(frame);
  const dir = view.getUint8(HEADER_BYTES + 8);
  if (!isDirection(dir)) {
    return { kind: "drop", reason: "direction" };
  }
  const flags = view.getUint8(HEADER_BYTES + 9);
  if ((flags & ~KNOWN_FLAGS) !== 0) {
    return { kind: "drop", reason: "flags" };
  }
  out.seq = view.getUint32(HEADER_BYTES, true);
  out.ackTick = view.getUint32(HEADER_BYTES + 4, true);
  out.dir = dir;
  out.flags = flags;
  return OK;
}
